import type { ComponentType } from 'react';
import React, { useEffect, useState } from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy } from '@confluence/loadable';

import type { WelcomeSeriesMountProps } from './WelcomeSeriesMount.tsx';

// No UFOLoadHold: an optional card must not hold page-load metrics.
const LazyWelcomeSeriesMount: LoadableType<WelcomeSeriesMountProps> = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-WelcomeSeriesMount" */ './WelcomeSeriesMount.tsx'))
			.WelcomeSeriesMount,
});

// A plain import, not a loadable: loadables queue behind the page's other loadables and hold
// page-load metrics while they wait.
const useLoomSuggestionsPreloader = (): ComponentType | null => {
	const [Preloader, setPreloader] = useState<ComponentType | null>(null);

	useEffect(() => {
		let isMounted = true;
		import(
			/* webpackChunkName: "loadable-LoomSuggestionsPreloader" */ './LoomSuggestionsPreloader.tsx'
		).then(
			({ LoomSuggestionsPreloader }) => {
				if (isMounted) {
					setPreloader(() => LoomSuggestionsPreloader);
				}
			},
			() => {},
		);
		return () => {
			isMounted = false;
		};
	}, []);

	return Preloader;
};

export const WelcomeSeriesMountLoader = (props: WelcomeSeriesMountProps): React.JSX.Element => {
	const LoomSuggestionsPreloader = useLoomSuggestionsPreloader();

	return (
		<>
			{LoomSuggestionsPreloader && (
				<TransparentErrorBoundary attribution={Attribution.CONTENT_TYPES}>
					<LoomSuggestionsPreloader />
				</TransparentErrorBoundary>
			)}
			<TransparentErrorBoundary attribution={Attribution.CONTENT_TYPES}>
				<LazyWelcomeSeriesMount {...props} />
			</TransparentErrorBoundary>
		</>
	);
};
